import { collection, doc, getDoc, getDocs, query, where, orderBy, limit, startAfter, onSnapshot, getCountFromServer } from "firebase/firestore";
import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import moment from 'moment';
import { EVENT_TYPES } from "@/constants/eventTypes";
import { getDb } from "@/firebase";

let unsubEvents = undefined;
let unsubUsers = undefined;

Vue.use(VueAxios, axios);

const getUserData = async (userId, db = null) => {
  try {
    if (!userId) {
      console.warn('getUserData called with undefined userId');
      return null;
    }
    const firestore = db || getDb();
    if (!firestore) {
      console.error('Firestore not available in getUserData');
      return userId;
    }
    const user = await getDoc(doc(firestore, 'users', userId));
    if (user.exists) {
      return { id: user.id, ...user.data() };
    }
    return userId;
  } catch (error) {
    console.error('Error in getUserData:', error);
    return userId; // Return the userId as fallback
  }
}

const stateCollections = {
  gender: 'byGender',
  fitnessLevel: 'byLevel',
  challenge: 'byChallenge',
}

export default {
  state: {
    byGender: {},
    byLevel: {},
    byChallenge: {},
  },
  mutations: {
    SET_COUNTERS(state, counters) {
      state[stateCollections[counters.type]] = counters.data
    }
  },
  actions: {
    async getTrends({ commit }, { startDate, endDate, types }) {
      try {
        const db = getDb();
        const requests = types.map(type => {
          return getCountFromServer(query(collection(db, 'inAppEvents'), where('createdAt', '>=', startDate), where('createdAt', '<=', endDate), where('eventType', '==', type)))
        })
        const result = await Promise.all(requests);

        // Create an object with types as keys and counts as values
        const trendsObject = {};
        types.forEach((type, index) => {
          trendsObject[type] = result[index].data().count;
        });

        return trendsObject;
      } catch (error) {
        console.error('Error in getTrends:', error);
        return types.reduce((acc, type) => {
          acc[type] = 0;
          return acc;
        }, {});
      }
    },
    async getActiveUsersChart({ commit }, { startDate, endDate }) {
      try {
        const res = await Vue.axios.post(
          "https://us-central1-maxfit-app.cloudfunctions.net/getActiveUsersChart",
          { startDate, endDate },
          { headers: null }
        );
        return res.data || [];
      } catch (error) {
        console.error("Error in getActiveUsersChart:", error);
        return [];
      }
    },
    async getAvgWorkoutTimeChart({ commit }, { startDate, endDate }) {
      try {
        const res = await Vue.axios.post(
          "https://us-central1-maxfit-app.cloudfunctions.net/getAvgWorkoutTimeChart",
          { startDate, endDate },
          { headers: null }
        );
        return res.data || { xValues: [], yValues: [], dateRange: {}, csvValues: [] };
      } catch (error) {
        console.error("Error in getAvgWorkoutTimeChart:", error);
        return { xValues: [], yValues: [], dateRange: { startDate, endDate }, csvValues: [] };
      }
    },
    async getActiveUsersList({ commit }, { startDate, endDate }) {
      try {
        const res = await Vue.axios.post(
          'https://us-central1-maxfit-app.cloudfunctions.net/getActiveUsersList',
          { startDate, endDate },
          { headers: null }
        );
        console.log('res', res.data);
        return res.data || [];
      } catch (error) {
        console.error('Error in getActiveUsersList:', error);
        return [];
      }
    },
    async getActiveUsers({ commit }, { startDate, endDate }) {
      try {
        const db = getDb();
        if (!db) {
          console.error('Firebase Firestore not initialized in getActiveUsers');
          return 0;
        }

        // Query for App opened events in the date range
        const rangeStart = moment(startDate).startOf('day').toDate();
        const rangeEnd = moment(endDate).endOf('day').toDate();

        const appOpenedEventsQuery = query(
          collection(db, 'inAppEvents'),
          where('createdAt', '>=', rangeStart),
          where('createdAt', '<=', rangeEnd),
          where('eventType', '==', EVENT_TYPES.APP_OPENED)
        );

        // Fetch App opened events
        const appOpenedEventsSnapshot = await getDocs(appOpenedEventsQuery);

        // Helper function to extract userId from event
        const getUserIdFromEvent = (eventData) => {
          // Try userId first (string)
          if (eventData.userId && typeof eventData.userId === 'string') {
            return eventData.userId;
          }
          // Try user field (could be string or reference)
          if (eventData.user) {
            if (typeof eventData.user === 'string') {
              return eventData.user;
            }
            // If it's a reference, get the ID
            if (eventData.user.id) {
              return eventData.user.id;
            }
          }
          return null;
        };

        // Use a Set to track unique users (one count per user regardless of how many events)
        const uniqueUsers = new Set();

        // Process App opened events - collect unique user IDs
        for (const eventDoc of appOpenedEventsSnapshot.docs) {
          const eventData = eventDoc.data();
          const userId = getUserIdFromEvent(eventData);
          if (userId) {
            uniqueUsers.add(userId);
          }
        }

        return uniqueUsers.size;
      } catch (error) {
        console.error('Error in getActiveUsers:', error);
        return 0;
      }
    },
    async getWorkoutData({ commit }, { workoutId, type, userId }) {
      try {
        const db = getDb();
        if (!db) {
          console.error('Firebase Firestore not initialized in getWorkoutData');
          return undefined;
        }

        switch (type) {
          case 'coachedClass':
            const coachedClass = await getDoc(doc(db, 'proTrainersList', workoutId));
            if (coachedClass.exists) {
              return { ...coachedClass.data(), id: coachedClass.id };
            }
          case 'live':
            const live = await getDoc(doc(db, 'livesList', workoutId));
            if (live.exists) {
              return { ...live.data(), id: live.id };
            }
          case 'byo':
            const userbyo = await getDoc(doc(db, 'users', userId, 'byoSessions', workoutId));
            if (userbyo.exists) {
              return { ...userbyo.data(), id: userbyo.id };
            }
            const byoWorkout = await getDoc(doc(db, 'sharedByoList', workoutId));
            if (byoWorkout.exists) {
              return { ...byoWorkout.data(), id: byoWorkout.id };
            }
          case 'preSet':
            const preSetWorkout = await getDoc(doc(db, 'preSetsList', workoutId));
            if (preSetWorkout.exists) {
              return { ...preSetWorkout.data(), id: preSetWorkout.id };
            }
        }
        return undefined;
      } catch (error) {
        console.error('Error in getWorkoutData:', error);
        return undefined;
      }
    },
    async getEventsByUser({ commit }, filters = {}) {
      try {
        const { userId, limitNumber, lastDocId } = filters;
        console.log('limitParam', limitNumber);
        // Ensure Firebase is initialized
        const db = getDb();
        if (!db) {
          throw new Error('Firebase Firestore not initialized');
        }

        let constraints = [where('user', '==', userId), orderBy('createdAt', 'desc')];

        if (limitNumber) {
          constraints.push(limit(limitNumber));
        }

        if (lastDocId) {
          const lastDoc = await getDoc(doc(db, 'inAppEvents', lastDocId));
          if (lastDoc.exists) {
            constraints.push(startAfter(lastDoc));
          }
        }

        let q = query(collection(db, 'inAppEvents'), ...constraints);

        const events = await getDocs(q);
        const eventsData = await Promise.all(events.docs.map(async (e) => {
          const eventData = { id: e.id, ...e.data() };
          return eventData;
        }));

        return eventsData;
      } catch (error) {
        console.error('Error in getEventsByUser:', error);
        throw error;
      }
    },
    async getEvents({ commit }, filters = {}) {
      try {
        const { startDate, endDate, type, types, populateUser, workoutType } = filters;

        if (!startDate || !endDate) {
          throw new Error('startDate and endDate are required');
        }

        // Ensure Firebase is initialized
        const db = getDb();
        if (!db) {
          throw new Error('Firebase Firestore not initialized');
        }

        let constraints = [
          where('createdAt', '>=', startDate),
          where('createdAt', '<=', endDate),
          orderBy('createdAt', 'desc')
        ];

        if (type) {
          constraints.push(where('eventType', '==', type));
        }

        if (types) {
          constraints.push(where('eventType', 'in', types));
        }

        if (type === EVENT_TYPES.WORKOUT_COMPLETED && workoutType) {
          constraints.push(where('type', '==', workoutType));
        }

        let q = query(collection(db, 'inAppEvents'), ...constraints);

        const events = await getDocs(q);

        const eventsData = await Promise.all(events.docs.map(async (e) => {
          try {
            const eventData = { id: e.id, ...e.data() };
            if (populateUser && eventData.user && eventData.user.length > 0) {
              const userData = await getUserData(eventData.user, db);
              eventData.user = userData;
            }
            if (populateUser && eventData.userId && eventData.userId.length > 0) {
              const userData = await getUserData(eventData.userId, db);
              eventData.user = userData;
            }
            return eventData;
          } catch (error) {
            console.error('Error processing event:', e.id, error);
            return { id: e.id, ...e.data() }; // Return event without user data if there's an error
          }
        }));

        return eventsData;
      } catch (error) {
        console.error('Error in getEvents:', error);
        throw error;
      }
    },
    // Note: despite the name, this now performs a one-off query instead of a real-time listener.
    // The component can call this periodically (e.g. every 60s) to poll current live users.
    async addLiveUsersListener({ commit }, onUsersChange) {
      try {
        const db = getDb();
        if (!db) {
          console.error('Firebase Firestore not initialized in addLiveUsersListener');
          return;
        }

        unsubUsers = query(
          collection(db, "users"),
          where('isOnline', '==', true),
          limit(100)
        );
        unsubUsers = onSnapshot(unsubUsers, async (querySnapshot) => {
          try {
            const docs = await Promise.all(querySnapshot.docs.map(async (e) => {

              const now = moment().subtract(1, 'minute').toDate();

              const lastActive = e.data().lastActive.toDate();

              if (lastActive && moment(lastActive).isBefore(now)) {
                return null;
              }
              return { id: e.id, ...e.data() };
            }));
            onUsersChange(docs.filter(doc => doc !== null));
          } catch (error) {
            console.error('Error in addLiveUsersListener:', error);
          }
        });
      } catch (error) {
        console.error('Error in addLiveUsersListener:', error);
      }
    },
    removeLiveUsersListener({ commit }) {
      if (unsubUsers && typeof unsubUsers === 'function') {
        unsubUsers();
        unsubUsers = undefined;
      }
    },
    addInAppEventsListener({ commit }, onEventsChange) {
      try {
        const db = getDb();
        if (!db) {
          console.error('Firebase Firestore not initialized in addInAppEventsListener');
          return;
        }

        const q = query(collection(db, "inAppEvents"), where('visible', '==', true), orderBy('createdAt', 'desc'), limit(100));
        unsubEvents = onSnapshot(q, async (querySnapshot) => {
          try {
            const docs = await Promise.all(querySnapshot.docs.map(async (e) => {
              try {
                const eventData = { id: e.id, ...e.data() };
                if (eventData.user && eventData.user.length > 0) {
                  const userData = await getUserData(eventData.user, db);
                  eventData.user = userData;
                }
                return eventData;
              } catch (error) {
                console.error('Error processing event in listener:', e.id, error);
                return { id: e.id, ...e.data() }; // Return event without user data if there's an error
              }
            }));
            onEventsChange(docs);
          } catch (error) {
            console.error(error);
          }
        });
      } catch (error) {
        console.error('Error setting up in-app events listener:', error);
      }
    },
    removeInAppEventsListener({ commit }) {
      if (unsubEvents && typeof unsubEvents === 'function') {
        unsubEvents();
        unsubEvents = undefined;
      }
    },
    async getCurrentPremiums({ commit }, comparedDate) {
      try {
        const db = getDb();
        if (!db) {
          console.error('Firebase Firestore not initialized in getCurrentPremiums');
          return null;
        }

        const todayStart = moment().startOf('day').toDate();
        const todayEnd = moment().endOf('day').toDate();
        const today = await getDocs(query(collection(db, 'premiumsNumbers'), where('createdAt', '<=', todayEnd), where('createdAt', '>=', todayStart)));
        const todayItem = today.docs[0];
        const todayData = { ...todayItem.data(), id: todayItem.id };

        let comparedStart;
        let comparedEnd;

        if (comparedDate) {
          comparedStart = moment(comparedDate).startOf('day').toDate();
          comparedEnd = moment(comparedDate).endOf('day').toDate();
        } else {
          comparedStart = moment().subtract(1, 'day').startOf('day').toDate();
          comparedEnd = moment().subtract(1, 'day').endOf('day').toDate();
        }

        const compared = await getDocs(query(collection(db, 'premiumsNumbers'), where('createdAt', '<=', comparedEnd), where('createdAt', '>=', comparedStart)));
        const comparedItem = compared.docs[0];
        const comparedData = { ...comparedItem.data(), id: comparedItem.id };

        const difference = todayData.current - comparedData.current;
        const differencePercent = (100 / comparedData.current) * difference;

        return {
          today: todayData,
          compared: comparedData,
          comparedDate: comparedStart,
          difference,
          differencePercent
        }
      } catch (error) {
        console.error('Error in getCurrentPremiums:', error);
        return null;
      }
    },

    async getEventsNumbers({ commit }, { startDate, endDate, type }) {
      try {
        const db = getDb();
        if (!db) {
          console.error('Firebase Firestore not initialized in getEventsNumbers');
          return [];
        }

        const xValues = [];
        const yValues = [];
        const csvValues = [];

        const getDatesFromRange = (startDate, endDate, format = 'YYYY-MM-DD') => {
          const start = moment(startDate);
          const end = moment(endDate);
          const dates = [];

          // Validate inputs
          if (!start.isValid() || !end.isValid()) {
            console.error('Invalid date provided to getDatesFromRange');
            return [];
          }

          if (start.isAfter(end)) {
            console.error('Start date cannot be after end date');
            return [];
          }

          // Generate array of dates
          let current = start.clone();
          while (current.isSameOrBefore(end, 'day')) {
            dates.push(current.format(format));
            current.add(1, 'day');
          }

          return dates;
        };

        const dates = getDatesFromRange(startDate, endDate);

        console.log('dates', dates);

        await Promise.all(dates.map(async (date, index) => {
          const start = moment(date).startOf('day').toDate();
          const end = moment(date).endOf('day').toDate();

          const result = await getCountFromServer(query(collection(db, 'inAppEvents'), where('createdAt', '>=', start), where('createdAt', '<=', end), where('eventType', '==', type)));
          const value = result.data().count;

          xValues[index] = end.toLocaleDateString();
          yValues[index] = value;
          csvValues[index] = { Date: end.toLocaleDateString(), [type]: value };
        }));

        return {
          yValues,
          xValues,
          dateRange: {
            startDate: startDate,
            endDate: endDate
          },
          csvValues
        }


      } catch (error) {
        console.error('Error in getEventsNumbers:', error);
        return {
          yValues: [],
          xValues: [],
          dateRange: {
            startDate: startDate,
            endDate: endDate
          },
          csvValues: []
        };
      }
    },
    async getFinishedSubscriptionsNumbers({ commit }, { startDate, endDate, collectionName }) {
      try {
        const db = getDb();
        if (!db) {
          console.error('Firebase Firestore not initialized in getFinishedSubscriptionsNumbers');
          return {
            yValues: [],
            xValues: [],
            dateRange: { startDate, endDate },
            csvValues: []
          };
        }

        const xValues = [];
        const yValues = [];
        const csvValues = [];

        const getDatesFromRange = (startDate, endDate, format = 'YYYY-MM-DD') => {
          const start = moment(startDate);
          const end = moment(endDate);
          const dates = [];

          if (!start.isValid() || !end.isValid()) {
            console.error('Invalid date provided to getDatesFromRange');
            return [];
          }

          if (start.isAfter(end)) {
            console.error('Start date cannot be after end date');
            return [];
          }

          let current = start.clone();
          while (current.isSameOrBefore(end, 'day')) {
            dates.push(current.format(format));
            current.add(1, 'day');
          }

          return dates;
        };

        const dates = getDatesFromRange(startDate, endDate);

        await Promise.all(dates.map(async (date, index) => {
          try {
            // Query the collection by date field (string format "YYYY-MM-DD")
            const q = query(
              collection(db, collectionName),
              where('date', '==', date)
            );
            const result = await getDocs(q);

            // Sum up all the count values for this date (in case there are multiple docs)
            let value = 0;
            result.docs.forEach(doc => {
              const docData = doc.data();
              value += docData.count || 0;
            });

            const displayDate = moment(date).toDate().toLocaleDateString();
            xValues[index] = displayDate;
            yValues[index] = value;
            csvValues[index] = { Date: displayDate, [collectionName]: value };
          } catch (error) {
            console.error(`Error querying ${collectionName} for date ${date}:`, error);
            const displayDate = moment(date).toDate().toLocaleDateString();
            xValues[index] = displayDate;
            yValues[index] = 0;
            csvValues[index] = { Date: displayDate, [collectionName]: 0 };
          }
        }));

        return {
          yValues,
          xValues,
          dateRange: {
            startDate: startDate,
            endDate: endDate
          },
          csvValues
        };

      } catch (error) {
        console.error('Error in getFinishedSubscriptionsNumbers:', error);
        return {
          yValues: [],
          xValues: [],
          dateRange: {
            startDate: startDate,
            endDate: endDate
          },
          csvValues: []
        };
      }
    },
    async getEventsNumbersByWorkoutType({ commit }, { startDate, endDate, eventType, workoutType }) {
      try {
        const db = getDb();
        if (!db) {
          console.error('Firebase Firestore not initialized in getEventsNumbersByWorkoutType');
          return [];
        }

        const xValues = [];
        const yValues = [];
        const csvValues = [];

        const getDatesFromRange = (startDate, endDate, format = 'YYYY-MM-DD') => {
          const start = moment(startDate);
          const end = moment(endDate);
          const dates = [];

          // Validate inputs
          if (!start.isValid() || !end.isValid()) {
            console.error('Invalid date provided to getDatesFromRange');
            return [];
          }

          if (start.isAfter(end)) {
            console.error('Start date cannot be after end date');
            return [];
          }

          // Generate array of dates
          let current = start.clone();
          while (current.isSameOrBefore(end, 'day')) {
            dates.push(current.format(format));
            current.add(1, 'day');
          }

          return dates;
        };

        const dates = getDatesFromRange(startDate, endDate);

        console.log('dates for workout type query', dates);

        await Promise.all(dates.map(async (date, index) => {
          const start = moment(date).startOf('day').toDate();
          const end = moment(date).endOf('day').toDate();

          // Query events with both eventType and workoutType filters
          const result = await getCountFromServer(query(
            collection(db, 'inAppEvents'),
            where('createdAt', '>=', start),
            where('createdAt', '<=', end),
            where('eventType', '==', eventType),
            where('type', '==', workoutType)
          ));
          const value = result.data().count;

          xValues[index] = end.toLocaleDateString();
          yValues[index] = value;
          csvValues[index] = { Date: end.toLocaleDateString(), [`${eventType}_${workoutType}`]: value };
        }));

        return {
          yValues,
          xValues,
          dateRange: {
            startDate: startDate,
            endDate: endDate
          },
          csvValues
        }

      } catch (error) {
        console.error('Error in getEventsNumbersByWorkoutType:', error);
        return {
          yValues: [],
          xValues: [],
          dateRange: {
            startDate: startDate,
            endDate: endDate
          },
          csvValues: []
        };
      }
    },
    async getNewPremiums({ commit }, dateRange) {
      try {
        const db = getDb();
        if (!db) {
          console.error('Firebase Firestore not initialized in getNewPremiums');
          return [];
        }

        // const today = moment().startOf('day');
        // const before = today.clone().subtract(15, 'days').toDate();
        // const after = today.clone().add(15, 'days').endOf('day').toDate();

        let today;
        let before;

        if (dateRange) {
          today = moment(dateRange.endDate).endOf('day').toDate();
          before = moment(dateRange.startDate).startOf('day').toDate();
        } else {
          today = moment().toDate();
          before = moment().subtract(30, 'days').toDate();
        }

        const result = await getDocs(query(collection(db, 'premiumsNumbers'), where('createdAt', '<=', today), where('createdAt', '>=', before)));

        const xValues = [];
        const yValues = [];
        const csvValues = [];
        result.docs.map(e => {
          const itemData = { ...e.data(), id: e.id };
          const value = itemData.current;
          const label = itemData.createdAt.toDate().toLocaleDateString();
          csvValues.push({ Date: label, PremiumUsers: value });
          xValues.push(label);
          yValues.push(value);
        });

        return {
          yValues,
          xValues,
          dateRange: {
            startDate: before,
            endDate: today
          },
          csvValues
        }
      } catch (error) {
        return [];
      }
    },
    GetCounterAnalitycs({ commit }, type) {
      return new Promise((resolve, reject) => {
        try {
          const db = getDb();
          if (!db) {
            console.error('Firebase Firestore not initialized in GetCounterAnalitycs');
            reject(new Error('Firebase Firestore not initialized'));
            return;
          }

          getDoc(doc(db, "countersAnalytics", type))
            .then((res) => {
              let counters = res.data()
              commit('SET_COUNTERS', { data: counters, type: type })
              resolve(counters)
            })
            .catch((err) => {
              reject(err)
            })
        } catch (error) {
          reject(error)
        }
      })
    },
    getUserResults({ commit }, userId) {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getUserResults', { userId }, { headers: null });
          resolve(res.data);
        } catch (error) {
          reject(error)
          console.error(error);
        }
      })
    },
    getWorkoutsInfo({ commit }, type) {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getWorkoutsInfo', null, { headers: null });
          resolve(res.data);
        } catch (error) {
          reject(error)
          console.error(error);
        }
      })
    },
    getTopTen({ commit }, type) {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getTopTen', null, { headers: null });
          resolve(res.data);
        } catch (error) {
          reject(error)
          console.error(error);
        }
      })
    },
    getPresetsInfo({ commit }, type) {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getPresetsInfo', null, { headers: null });
          resolve(res.data);
        } catch (error) {
          reject(error)
          console.error(error);
        }
      })
    },
    getExercisesData({ commit }, type) {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getNewWorkoutsData', null, { headers: null });
          resolve(res.data);
        } catch (error) {
          reject(error)
          console.error(error);
        }
      })
    },
    getWorkoutsData({ commit }, type) {
      return new Promise(async (resolve, reject) => {
        try {
          // const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getUsersWorkoutsInfo', null, { headers: null });

          // const db = admin.firestore();
          const db = getDb();
          if (!db) {
            console.error('Firebase Firestore not initialized in getWorkoutsData');
            resolve([]);
            return;
          }

          const usersCollection = collection(db, 'users');

          const users = await getDocs(query(usersCollection, where('firstTimeInApp', '==', true)));
          const usersData = users.docs.map(u => u.id);

          let rows = [];

          function sliceIntoChunks(arr, chunkSize) {
            const res = [];
            for (let i = 0; i < arr.length; i += chunkSize) {
              const chunk = arr.slice(i, i + chunkSize);
              res.push(chunk);
            }
            return res;
          }

          const parts = sliceIntoChunks(usersData, 100);
          // const parts = [['9nw0Q8BUEIczpHznvs3mi0DRFNC3']]

          for (let index = 0; index < parts.length; index++) {

            const element = parts[index];
            // console.log(element.length, element.includes(undefined));

            const elementRows = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getUsersWorkoutsInfo', { users: element }, { headers: null });

            const newRows = elementRows.data;
            // console.log('rows: ', newRows.length);

            rows = rows.concat(newRows);
            console.log(((usersData / parts.length) * (index + 1)).toFixed(1));
          }
          resolve(rows);
        } catch (error) {
          reject(error)
          console.error(error);
        }
      })
    },
    getProgramUsers({ commit }, type) {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getProgramUsers', null, { headers: null });
          resolve(res.data);
        } catch (error) {
          reject(error)
          console.error(error);
        }
      })
    },
    getFreeUsersData({ commit }, type) {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getFreeUsersInfo', null, { headers: null });
          resolve(res.data);
        } catch (error) {
          reject(error)
          console.error(error);
        }
      })
    },
    getUsersData({ commit }, type) {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getUsersData', null, { headers: null });
          console.log(res.data.length, res.data[res.data.length - 1].id);
          const last = res.data[res.data.length - 1];
          const res2 = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getUsersData', { last: last.uid }, { headers: null });
          console.log(res2.data.length, res2.data[res.data.length - 1].id);
          resolve([...res.data, ...res2.data]);
        } catch (error) {
          reject(error)
          console.error(error);
        }
      })
    },
    extractSubInfo({ commit }, type) {
      return new Promise(async (resolve, reject) => {
        try {
          const times = 6; //x10,000 times to accomplish total of users
          let last = undefined;

          for (let index = 0; index < times; index++) {
            console.log('LAST: ', last);

            if (last) {
              const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/extractSubInfo', { last }, { headers: null });
              last = res.data;
            } else {
              const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/extractSubInfo', null, { headers: null });
              last = res.data;
            }
          }

          resolve('ok');
        } catch (error) {
          reject(error)
          console.error(error);
        }
      })
    },
    fixPosts({ commit }, type) {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/fixPostBeforeAlgolia', null, { headers: null });
          resolve(res.data);
        } catch (error) {
          reject(error)
          console.error(error);
        }
      })
    },
    getJanuaryPosts({ commit }, type) {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getPostsByRange', null, { headers: null });
          resolve(res.data);
        } catch (error) {
          reject(error)
          console.error(error);
        }
      })
    },
    async fixMaxMXP({ commit }, type) {
      try {
        let lastId = undefined;
        for (let index = 0; index < 56; index++) {
          const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/fixMaxMXP', { lastId }, { headers: null });
          lastId = res.data.lastProcessedId;
          console.log(lastId);
        }
        return 'done.';
      } catch (error) {
        console.error(error);
      }
    },
    async getSharedsForCsv({ commit }, type) {
      try {
        const res = await Vue.axios.post('https://us-central1-maxfit-app.cloudfunctions.net/getSharedsForCsv', null, { headers: null });
        return res.data;
      } catch (error) {
        console.error(error);
        return [];
      }
    }
  },
  getters: {
    getCounterByGender(state) {
      return state.byGender
    },
    getCounterByLevel(state) {
      return state.byLevel
    },
    getCounterByChallenge(state) {
      return state.byChallenge
    },
  },
};
